import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';

import { Observable, from, throwError } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { AmplifyService } from './amplify.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { PrintAuthTokenService } from './print-auth-token.service';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';
export const PrintAccessTokenStorageKey = 'print_access_token';

@Injectable({
	providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private amplifyService: AmplifyService,
		private readonly printAuthTokenService: PrintAuthTokenService,
		@Inject(PLATFORM_ID) private readonly platformId: string,
		@Optional() @Inject(REQUEST) private readonly request?: Request
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (isPlatformServer(this.platformId)) {
			let headers = request.headers;
			const requestOrigin = this.request ? `${this.request.protocol}://${this.request.get('host')}` : null;
			if (requestOrigin) {
				headers = headers.append('X-MBC-Origin', requestOrigin);
			}
			request = request.clone({ headers });
		}

		if (request.headers.has(InterceptorSkipHeader)) {
			return next.handle(this.clearHeaders(request));
		}

		const printToken = this.getPrintAccessToken();
		if (printToken && !request.headers.has('Authorization')) {
			const headers = request.headers.append('Authorization', `Bearer ${printToken}`).delete(InterceptorSkipHeader);
			request = request.clone({ headers });
			return next.handle(request);
		}

		if (isPlatformServer(this.platformId)) {
			return next.handle(request);
		}

		return from(this.amplifyService.isAuthenticated()).pipe(
			mergeMap(isAuthenticate => {
				if (isAuthenticate) {
					return this.amplifyService.getJwt().pipe(
						mergeMap(jwt => {
							return this.setJwtOrSkip(request, next, jwt);
						}),
						catchError(err => {
							if (err.status === 0) {
								return throwError(err);
							}
							return throwError(err);
						})
					);
				}
				const headers = request.headers.delete(InterceptorSkipHeader);
				request = request.clone({ headers });
				return next.handle(request);
			})
		);
	}

	private getPrintAccessToken(): string | null {
		const sharedToken = this.printAuthTokenService.getToken();
		if (sharedToken) {
			return sharedToken;
		}

		if (!isPlatformBrowser(this.platformId)) {
			return null;
		}

		try {
			return localStorage.getItem(PrintAccessTokenStorageKey);
		} catch (error) {
			return null;
		}
	}

	private clearHeaders(request: HttpRequest<any>): HttpRequest<any> {
		const headers = request.headers.delete(InterceptorSkipHeader);
		request = request.clone({ headers });
		return request;
	}

	private setJwtOrSkip(request: HttpRequest<any>, next: HttpHandler, jwt: string): Observable<HttpEvent<any>> {
		let headers = request.headers;

		if (!request.headers.has(InterceptorSkipHeader)) {
			headers = headers.append('Authorization', `Bearer ${jwt}`);
		}

		headers = headers.delete(InterceptorSkipHeader);
		request = request.clone({ headers });

		return next.handle(request);
	}
}
